import { FC, useState } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import Button from "components/particles/primary-button";
import { Container, StyledReactModal, Tbody } from "./style";
import unlink from "assets/images/unlink.svg";
import NoDataFound from "components/particles/no-data-found";
import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import { useSelector } from "react-redux";
import useCategories from "containers/categories/useHook";
import Swal from "sweetalert2";
import { LinkedCategoryIngredientsDTO } from "utils/helpers/models/categories.dto";
import placeHolder from "assets/images/placeholder.jpg";

interface LinkedIngredientModelProps {
  id: number;
  name?: string;
  data: any[];
  setData: any;
  pagination?: any;
  setPagination?: Function;
  limit?: number;
  onRefresh?: () => void;
}

const LinkedIngredientModal: FC<LinkedIngredientModelProps> = ({
  id,
  name,
  data,
  setData,
  pagination,
  setPagination,
  limit = 10,
  onRefresh,
}) => {
  const { linkedIngredientModalIsOpen, isLoading } = useSelector(
    (state: any) => state.user
  );
  const {
    unLinkedCategoryIngredients,
    searchLinkedIngredeints,
    getCategoryLinkedIngredientById,
  } = useCategories();
  const [searchData, setSearchData] = useState<any[]>([]);
  const [searchPagination, setSearchPagination] = useState<any>({});
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState({
    name: "",
    email: "",
    gender: "",
  });

  function closeModal() {
    dispatch(setModalClosed("Linked Ingredient"));
  }

  const search = () => {
    if (searchQuery.name.trim() !== "") {
      setIsSearchActive(true);
      setSearchData([]);
      searchLinkedIngredeints(
        id,
        searchQuery.name,
        setSearchData,
        setSearchPagination,
        0,
        limit
      );
    } else {
      setIsSearchActive(false);
      setSearchData([]);
      setSearchPagination({});
    }
  };

  const reset = () => {
    setSearchQuery({ name: "", email: "", gender: "" });
    setSearchData([]);
    setSearchPagination({});
    setIsSearchActive(false);
  };

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      if (isSearchActive) {
        const nextOffset = searchData.length;
        await searchLinkedIngredeints(
          id,
          searchQuery.name,
          setSearchData,
          setSearchPagination,
          nextOffset,
          limit,
          true
        );
      } else {
        const nextOffset = data.length;
        await getCategoryLinkedIngredientById(
          id,
          setData,
          setPagination,
          nextOffset,
          limit,
          true
        );
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const handleUnLinkedIngredient = (associationId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Unlink this ingredient from the category?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--reset-button)",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, unlink it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await unLinkedCategoryIngredients(associationId, data, setData);

          const updatedData = data.filter(
            (ingredient: any) => ingredient.associationId !== associationId
          );
          setData(updatedData);
          setSearchData((prev) =>
            prev.filter(
              (ingredient: any) => ingredient.associationId !== associationId
            )
          );

          if (setPagination && pagination?.total) {
            setPagination({
              ...pagination,
              total: Math.max(0, (pagination.total ?? 0) - 1),
            });
          }
          if (isSearchActive && searchPagination?.total) {
            setSearchPagination({
              ...searchPagination,
              total: Math.max(0, (searchPagination.total ?? 0) - 1),
            });
          }

          if (onRefresh) {
            onRefresh();
          }
        } catch (error) {
          console.error("Error unlinking ingredient:", error);
        }
      }
    });
  };

  const columns: any[] = [
    {
      name: "Sr No.",
      class: "",
    },
    {
      name: "Image",
      class: "",
    },
    {
      name: "Ingredient Name",
      class: "",
    },
    {
      name: "Actions",
      class: "",
    },
  ];

  const displayedData = isSearchActive ? searchData : data;
  const activePagination = isSearchActive ? searchPagination : pagination;
  const totalRecords = activePagination?.total ?? 0;
  const hasMore =
    totalRecords > 0 && displayedData.length < totalRecords && !loadingMore;

  return (
    <StyledReactModal
      isOpen={linkedIngredientModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Linked Ingredient Modal"
    >
      <Container>
        <div className="close-button" onClick={closeModal}>
          <img src={close} style={{ cursor: "pointer" }} alt="img" />
        </div>
        <div className="popup-title">
          <h1>
            {name
              ? `Linked Ingredients - ${name}`
              : "Linked Category Ingredients"}
          </h1>
        </div>
        <div className="search-container">
          <div className="input-fields">
            <input
              type="text"
              placeholder="Search By Name"
              value={searchQuery.name}
              onChange={(e) => {
                setSearchQuery({ ...searchQuery, name: e.target.value });
              }}
            />
          </div>
          <Button title="Search" width="16rem" fill="true" onclick={search} />
          <Button
            title="Reset"
            width="16rem"
            fill="true"
            backgroundcolor="var(--reset-button)"
            onclick={reset}
          />
        </div>
        <div className="inner-container">
          <div className="categories-model-ingredient-container p-custom-scrollbar-8 custom-scrollbar">
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    {columns.map((column: any, index: number) => {
                      return (
                        <th key={index} className={column.class}>
                          {column?.name}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <Tbody>
                  {displayedData?.length > 0 ? (
                    displayedData?.map(
                      (
                        ingredient: LinkedCategoryIngredientsDTO,
                        index: any
                      ) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <img
                              src={
                                ingredient?.image && !ingredient?.image.includes(`${process.env.REACT_APP_BASE_URL}images/placeholder`)
                                  ? ingredient?.image
                                  : placeHolder
                              }
                              alt="img"
                              width={30}
                              style={{ borderRadius: "50%" }}
                            />
                          </td>
                          <td>{ingredient?.name}</td>
                          <td className="actions">
                            <button
                              onClick={() =>
                                handleUnLinkedIngredient(
                                  ingredient?.associationId
                                )
                              }
                              className="tooltip link-button"
                            >
                              <img src={unlink} alt="img" />
                              <p>Unlink</p>
                            </button>
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <NoDataFound isShow={!isLoading} />
                  )}
                </Tbody>
              </table>
            </div>
          </div>
          {hasMore && (
            <div className="load-more-container">
              <Button
                title={loadingMore ? "Loading..." : "Load More"}
                width="20rem"
                fill="true"
                onclick={handleLoadMore}
              />
            </div>
          )}
        </div>
      </Container>
    </StyledReactModal>
  );
};

export default LinkedIngredientModal;
